import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { usePaymentStatus } from "@/lib/hooks/use-payment-status";
import type { MsiOptions } from "@/lib/schemas/paycode/msi-options";
import {
  useCheckoutStore,
  useSelectedPaymentMethod,
} from "@/lib/store/checkout";
import { CashInstructions } from "./instructions/cash";
import { CieInstructions } from "./instructions/cie";
import { CodiInstructions } from "./instructions/codi";
import { SpeiInstructions } from "./instructions/spei";
import { TpvBarcodeInstructions } from "./instructions/tpv-barcode";
import PaymentForm from "./payment-form";
import { PaymentMethodPicker } from "./payment-method-picker";

type PaymentStepProps = {
  msi?: MsiOptions | null;
  embedded?: boolean;
};

export const PaymentStep = ({ msi, embedded = false }: PaymentStepProps) => {
  const total = useCheckoutStore((s) => s.total);
  const hash = useCheckoutStore((s) => s.hash);
  const redirectUrl = useCheckoutStore((s) => s.redirectUrl);
  const card = useCheckoutStore((s) => s.card);
  const linkType = useCheckoutStore((s) => s.linkType);
  const selectedMethod = useSelectedPaymentMethod();

  const router = useRouter();

  const handleSuccess = (transaction: string = "", delay = 2000) => {
    const encodedTrans = encodeURIComponent(transaction);
    setTimeout(() => {
      if (embedded)
        window.parent.postMessage(
          {
            type: "payment_success",
            payload: { redirectUrl: redirectUrl, cardNumber: card?.card },
          },
          "*",
        );
      else {
        if (redirectUrl) {
          window.onbeforeunload = null;
          window.location.assign(redirectUrl);
        } else router.push(`/${hash}/receipt?trackCode=${encodedTrans}`);
      }
    }, delay);
  };

  const handleReject = (message?: string) => {
    if (embedded) {
      window.parent.postMessage(
        { type: "payment_reject", payload: { message } },
        "*",
      );
    }
  };

  const isOffline =
    !!selectedMethod && selectedMethod !== "card" && linkType === "oneTime";
  const paidAt = usePaymentStatus(hash, isOffline);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once when paidAt flips truthy; handleSuccess re-runs would queue duplicate redirects
  useEffect(() => {
    if (paidAt) handleSuccess("OFFLINE", 0);
  }, [paidAt]);

  const renderPaymentMethod = () => {
    if (selectedMethod === "card")
      return (
        <PaymentForm
          amount={total}
          msiOptions={msi}
          onSuccess={handleSuccess}
          onReject={handleReject}
        />
      );
    if (selectedMethod === "cash") return <CashInstructions />;
    if (selectedMethod === "spei") return <SpeiInstructions />;
    if (selectedMethod === "cie") return <CieInstructions />;
    if (selectedMethod === "tpvBarcode") return <TpvBarcodeInstructions />;
    if (selectedMethod === "codi") return <CodiInstructions />;

    return <p>{selectedMethod}</p>;
  };

  return (
    <div className="space-y-8">
      <PaymentMethodPicker />
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          className="w-full pb-4"
          key={`payment-form-${selectedMethod}`}
          initial={{
            opacity: 0,
            transform: "translateY(-12px) scale(0.97)",
            filter: "blur(2px)",
          }}
          animate={{
            opacity: 1,
            transform: "translateY(0px) scale(1)",
            filter: "blur(0px)",
          }}
          exit={{
            opacity: 0,
            transform: "translateY(-12px) scale(0.97)",
            filter: "blur(2px)",
          }}
          transition={{
            duration: 0.18,
            ease: [0.19, 1, 0.22, 1],
          }}
        >
          {renderPaymentMethod()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
